import React, { useState } from 'react';
import api from '../utils/api';
import { Button, Field, Input } from './admin/ui';
import { IconAlert, IconCheckCircle, IconLock } from './icons';
import { fieldErrorsFrom } from '../utils/formErrors';

/**
 * Change-your-own-password form, shared by the admin console and the merchant
 * portal. Both post to the same endpoint, which requires the current password.
 */
const ChangePasswordForm = ({ onDone, compact = false }) => {
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [show, setShow] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    // Clear that field's message as soon as the user edits it.
    setErrors((current) => ({ ...current, [key]: undefined, _general: undefined }));
  };

  const validate = () => {
    const next = {};
    // Checked here because the server answers a missing current password with
    // "your current password is not correct", which reads as a wrong guess.
    if (!form.current) next.current = 'Enter your current password.';
    if (form.next.length < 8) next.next = 'Use at least 8 characters.';
    else if (form.next === form.current) next.next = 'Choose a password different from the current one.';
    if (!form.confirm) next.confirm = 'Re-enter the new password.';
    else if (form.next !== form.confirm) next.confirm = 'The two passwords do not match.';
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const found = validate();
    if (Object.keys(found).length) {
      setErrors(found);
      return;
    }
    setErrors({});

    setSaving(true);
    try {
      await api.post('/api/auth/change-password', {
        currentPassword: form.current,
        newPassword: form.next
      });
      setForm({ current: '', next: '', confirm: '' });
      setDone(true);
      onDone?.();
    } catch (err) {
      // Server messages land on the field that caused them.
      setErrors(
        fieldErrorsFrom(err, {
          fieldMap: { newPassword: 'next', currentPassword: 'current' },
          fallback: 'Could not change your password.'
        })
      );
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="flex items-start gap-3 rounded-xl bg-success-50 px-4 py-3.5 ring-1 ring-inset ring-success-100">
        <IconCheckCircle size={20} className="mt-0.5 shrink-0 text-success-600" />
        <div className="min-w-0">
          <p className="text-sm font-600 text-success-700">Password updated</p>
          <p className="mt-1 text-xs text-success-700/80">
            Any outstanding reset link has been cancelled. You stay signed in here.
          </p>
        </div>
        <Button variant="secondary" size="sm" className="ml-auto" onClick={() => setDone(false)}>
          Change again
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      {errors._general && (
        <div
          role="alert"
          className="flex items-start gap-2.5 rounded-lg bg-danger-50 px-3.5 py-3 ring-1 ring-inset ring-danger-100"
        >
          <IconAlert size={17} className="mt-0.5 shrink-0 text-danger-600" />
          <p className="text-sm text-danger-700">{errors._general}</p>
        </div>
      )}

      <Field label="Current password" htmlFor="cp-current" required error={errors.current}>
        <Input
          id="cp-current"
          type={show ? 'text' : 'password'}
          autoComplete="current-password"
          value={form.current}
          onChange={(e) => setField('current', e.target.value)}
        />
      </Field>

      <div className={compact ? 'space-y-5' : 'grid grid-cols-1 gap-5 sm:grid-cols-2'}>
        <Field
          label="New password"
          htmlFor="cp-next"
          required
          error={errors.next}
          hint="At least 8 characters."
        >
          <Input
            id="cp-next"
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            value={form.next}
            onChange={(e) => setField('next', e.target.value)}
          />
        </Field>

        <Field label="Confirm new password" htmlFor="cp-confirm" required error={errors.confirm}>
          <Input
            id="cp-confirm"
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            value={form.confirm}
            onChange={(e) => setField('confirm', e.target.value)}
          />
        </Field>
      </div>

      <label className="flex items-center gap-2.5 text-sm text-ink-600">
        <input
          type="checkbox"
          checked={show}
          onChange={(e) => setShow(e.target.checked)}
          className="h-4 w-4 rounded accent-brand-800"
        />
        Show passwords
      </label>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" loading={saving}>
          <IconLock size={16} />
          Update password
        </Button>
        <p className="text-xs text-ink-500">
          Changing your password cancels any outstanding reset link.
        </p>
      </div>
    </form>
  );
};

export default ChangePasswordForm;
